import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Check, X, Database, Edit, Trash2, Plus, FileJson, RefreshCw } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';

interface TableColumn {
  COLUMN_NAME: string;
  COLUMN_TYPE: string;
  IS_NULLABLE: string;
  COLUMN_KEY: string;
  COLUMN_DEFAULT: string | null;
  EXTRA: string;
}

interface TableData {
  data: any[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export default function DatabaseManagement() {
  const [tables, setTables] = useState<string[]>([]);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [tableStructure, setTableStructure] = useState<TableColumn[]>([]);
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [newRow, setNewRow] = useState<any | null>(null);
  const [sqlQuery, setSqlQuery] = useState<string>('');
  const [sqlResult, setSqlResult] = useState<any | null>(null);

  useEffect(() => {
    fetchTables();
  }, []);

  useEffect(() => {
    if (selectedTable) {
      fetchTableStructure(selectedTable);
      fetchTableData(selectedTable, 1);
    }
  }, [selectedTable]);

  const fetchTables = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/admin/database-tables', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch tables');
      const data = await response.json();
      setTables(data);
    } catch (error) {
      console.error('Error fetching tables:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch database tables"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTableStructure = async (tableName: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/admin/table-structure/${tableName}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch structure for ${tableName}`);
      const data = await response.json();
      setTableStructure(data);
    } catch (error) {
      console.error(`Error fetching structure for ${tableName}:`, error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to fetch structure for ${tableName}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTableData = async (tableName: string, page: number) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/admin/table-data/${tableName}?page=${page}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch data for ${tableName}`);
      const data = await response.json();
      setTableData(data);
      setCurrentPage(page);
    } catch (error) {
      console.error(`Error fetching data for ${tableName}:`, error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to fetch data for ${tableName}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTableSelect = (tableName: string) => {
    setSelectedTable(tableName);
    setEditingRow(null);
    setNewRow(null);
    setSqlResult(null);
  };

  const getPrimaryKeyColumn = () => {
    const pkColumn = tableStructure.find(col => col.COLUMN_KEY === 'PRI');
    return pkColumn ? pkColumn.COLUMN_NAME : 'id';
  };

  const handleEditRow = (row: any) => {
    setEditingRow({ ...row });
  };

  const handleSaveEdit = async () => {
    if (!selectedTable || !editingRow) return;
    
    setIsLoading(true);
    const pkColumn = getPrimaryKeyColumn();
    const id = editingRow[pkColumn];
    
    try {
      const response = await fetch(`/api/admin/table-data/${selectedTable}/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingRow)
      });

      if (!response.ok) throw new Error('Failed to update record');
      
      toast({
        title: "Success",
        description: "Record updated successfully"
      });
      
      fetchTableData(selectedTable, currentPage);
      setEditingRow(null);
    } catch (error) {
      console.error('Error updating record:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update record"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRow = async (row: any) => {
    if (!selectedTable) return;
    if (!confirm('Are you sure you want to delete this record? This action cannot be undone.')) return;
    
    setIsLoading(true);
    const pkColumn = getPrimaryKeyColumn();
    const id = row[pkColumn];
    
    try {
      const response = await fetch(`/api/admin/table-data/${selectedTable}/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete record');
      
      toast({
        title: "Success",
        description: "Record deleted successfully"
      });
      
      fetchTableData(selectedTable, currentPage);
    } catch (error) {
      console.error('Error deleting record:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete record"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNewRow = () => {
    // Create an empty object with the table's columns
    const newRowData: any = {};
    tableStructure.forEach(column => {
      newRowData[column.COLUMN_NAME] = null;
    });
    setNewRow(newRowData);
  };

  const handleSaveNewRow = async () => {
    if (!selectedTable || !newRow) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`/api/admin/table-data/${selectedTable}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newRow)
      });

      if (!response.ok) throw new Error('Failed to create record');
      
      toast({
        title: "Success",
        description: "Record created successfully"
      });
      
      fetchTableData(selectedTable, currentPage);
      setNewRow(null);
    } catch (error) {
      console.error('Error creating record:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create record"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const executeSQL = async () => {
    if (!sqlQuery.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "SQL query is required"
      });
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch('/api/admin/execute-sql', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sql: sqlQuery })
      });

      if (!response.ok) throw new Error('Failed to execute SQL');
      
      const result = await response.json();
      setSqlResult(result.result);
      
      toast({
        title: "Success",
        description: "SQL executed successfully"
      });
    } catch (error) {
      console.error('Error executing SQL:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to execute SQL query"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = (rowData: any, columnName: string, value: any) => {
    return {
      ...rowData,
      [columnName]: value
    };
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Database Management</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Left sidebar - Tables list */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Database className="h-5 w-5 mr-2" />
              Database Tables
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[500px] overflow-y-auto">
              <ul className="divide-y">
                {tables.map(table => (
                  <li key={table}>
                    <button
                      className={`w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                        selectedTable === table ? 'bg-gray-100 dark:bg-gray-800 font-medium' : ''
                      }`}
                      onClick={() => handleTableSelect(table)}
                    >
                      {table}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
        
        {/* Main content area */}
        <div className="col-span-1 md:col-span-3 space-y-6">
          {/* SQL Query Editor */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileJson className="h-5 w-5 mr-2" />
                SQL Query
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <textarea 
                  className="w-full h-24 p-2 border rounded-md font-mono text-sm"
                  value={sqlQuery}
                  onChange={(e) => setSqlQuery(e.target.value)}
                  placeholder="Enter SQL query here..."
                />
                <div className="flex justify-end">
                  <Button onClick={executeSQL} disabled={isLoading}>
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Execute Query
                  </Button>
                </div>
              </div>
              
              {sqlResult && (
                <div className="mt-4">
                  <h3 className="font-medium mb-2">Results:</h3>
                  {Array.isArray(sqlResult) ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          {sqlResult.length > 0 && (
                            <tr>
                              {Object.keys(sqlResult[0]).map(key => (
                                <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  {key}
                                </th>
                              ))}
                            </tr>
                          )}
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {sqlResult.map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                              {Object.values(row).map((value: any, colIdx) => (
                                <td key={colIdx} className="px-6 py-4 whitespace-nowrap text-sm">
                                  {value === null ? <span className="text-gray-400">NULL</span> : String(value)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-x-auto">
                      {JSON.stringify(sqlResult, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Table data display */}
          {selectedTable && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{selectedTable} Table</span>
                  <div className="flex items-center space-x-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => fetchTableData(selectedTable, currentPage)}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Refresh
                    </Button>
                    <Button size="sm" onClick={handleAddNewRow} disabled={!!newRow}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Record
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex justify-center py-10">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Table structure */}
                    <details className="mb-4">
                      <summary className="cursor-pointer font-medium py-2">Table Structure</summary>
                      <div className="overflow-x-auto mt-2">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Column</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Nullable</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Key</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Default</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Extra</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {tableStructure.map((column) => (
                              <tr key={column.COLUMN_NAME} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  {column.COLUMN_NAME}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  {column.COLUMN_TYPE}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  {column.IS_NULLABLE === 'YES' ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  {column.COLUMN_KEY === 'PRI' ? (
                                    <Badge variant="default">Primary</Badge>
                                  ) : column.COLUMN_KEY === 'UNI' ? (
                                    <Badge variant="outline">Unique</Badge>
                                  ) : column.COLUMN_KEY === 'MUL' ? (
                                    <Badge variant="secondary">Index</Badge>
                                  ) : null}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  {column.COLUMN_DEFAULT === null ? (
                                    <span className="text-gray-400">NULL</span>
                                  ) : (
                                    column.COLUMN_DEFAULT
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  {column.EXTRA}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                    
                    {/* Table data */}
                    {tableData && tableData.data.length > 0 ? (
                      <>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                {tableStructure.map((column) => (
                                  <th key={column.COLUMN_NAME} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    {column.COLUMN_NAME}
                                  </th>
                                ))}
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {/* New row form */}
                              {newRow && (
                                <tr className="bg-blue-50 dark:bg-blue-900/20">
                                  {tableStructure.map((column) => (
                                    <td key={column.COLUMN_NAME} className="px-6 py-4 whitespace-nowrap text-sm">
                                      <Input
                                        type="text"
                                        value={newRow[column.COLUMN_NAME] || ''}
                                        onChange={(e) => setNewRow(updateField(newRow, column.COLUMN_NAME, e.target.value))}
                                        disabled={column.EXTRA.includes('auto_increment')}
                                        className="w-full"
                                      />
                                    </td>
                                  ))}
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex justify-end space-x-2">
                                      <Button size="sm" onClick={handleSaveNewRow} disabled={isLoading}>
                                        <Check className="h-4 w-4 mr-1" />
                                        Save
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => setNewRow(null)}>
                                        <X className="h-4 w-4 mr-1" />
                                        Cancel
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              )}
                              
                              {/* Existing rows */}
                              {tableData.data.map((row, idx) => (
                                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                  {tableStructure.map((column) => (
                                    <td key={column.COLUMN_NAME} className="px-6 py-4 whitespace-nowrap text-sm">
                                      {editingRow && editingRow[getPrimaryKeyColumn()] === row[getPrimaryKeyColumn()] ? (
                                        <Input
                                          type="text"
                                          value={editingRow[column.COLUMN_NAME] || ''}
                                          onChange={(e) => setEditingRow(updateField(editingRow, column.COLUMN_NAME, e.target.value))}
                                          disabled={column.COLUMN_KEY === 'PRI' || column.EXTRA.includes('auto_increment')}
                                          className="w-full"
                                        />
                                      ) : (
                                        row[column.COLUMN_NAME] === null ? (
                                          <span className="text-gray-400">NULL</span>
                                        ) : (
                                          String(row[column.COLUMN_NAME])
                                        )
                                      )}
                                    </td>
                                  ))}
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    {editingRow && editingRow[getPrimaryKeyColumn()] === row[getPrimaryKeyColumn()] ? (
                                      <div className="flex justify-end space-x-2">
                                        <Button size="sm" onClick={handleSaveEdit} disabled={isLoading}>
                                          <Check className="h-4 w-4 mr-1" />
                                          Save
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => setEditingRow(null)}>
                                          <X className="h-4 w-4 mr-1" />
                                          Cancel
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex justify-end space-x-2">
                                        <Button size="sm" variant="outline" onClick={() => handleEditRow(row)}>
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteRow(row)}>
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Pagination */}
                        {tableData.pagination.pages > 1 && (
                          <div className="flex justify-between items-center mt-4">
                            <div className="text-sm text-gray-500">
                              Showing {(tableData.pagination.page - 1) * tableData.pagination.limit + 1} to {Math.min(tableData.pagination.page * tableData.pagination.limit, tableData.pagination.total)} of {tableData.pagination.total} results
                            </div>
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                disabled={currentPage === 1 || isLoading}
                                onClick={() => fetchTableData(selectedTable, currentPage - 1)}
                              >
                                Previous
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                disabled={currentPage === tableData.pagination.pages || isLoading}
                                onClick={() => fetchTableData(selectedTable, currentPage + 1)}
                              >
                                Next
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No data available in table
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}